import React, { useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import { SeriesData } from "../context/FileLibraryContext";
import { Link } from "react-router-dom";

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Typography,
} from "@mui/material";

import { IoLockClosedSharp } from "react-icons/io5";

import {
  WAKE_UP_RELEASE_TIME,
  WAKE_UP_SERIES_ID,
  isWakeUpReleased,
} from "../config/wakeUpRelease";

const ContentLibraryPage: React.FC = () => {
  const { fileLibrary, accessTier } = useAppContext();

  const [wakeUpReleased, setWakeUpReleased] = useState(
    isWakeUpReleased
  );

  useEffect(() => {
    document.title = "Videos | ipwd.club";

    // Keep the card synchronized if the page remains open
    // when the release time arrives.
    const updateReleaseStatus = () => {
      setWakeUpReleased(isWakeUpReleased());
    };

    updateReleaseStatus();

    const intervalId = window.setInterval(
      updateReleaseStatus,
      1000
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  function createVideoPreviewCard(
    series: SeriesData,
    index: number
  ) {
    let cardClassName = "video-preview-card";
    let linkPath = `/files/${series.id}`;

    if (series.seriesTier > accessTier) {
      cardClassName += " locked-content";
      linkPath = "#";
    }

    return (
      <Card key={series.id || index} className={cardClassName}>
        <CardActionArea
          className="video-preview-action-area"
          component={Link}
          to={linkPath}
        >
          <div className="video-preview-left">
            <Chip
              label={`${series.episodes.length} files`}
              className={`card-media-chip small-chip card-media-chip-${series.type}`}
            />

            <CardMedia
              component="img"
              sx={{ width: 151 }}
              image={series.cover}
              alt={series.name}
              className="video-preview-image"
            />
          </div>

          <Box className="video-preview-right">
            <CardContent sx={{ flex: "1 0 auto" }}>
              <Typography
                component="div"
                variant="h5"
                className="video-preview-title"
              >
                {series.name}
              </Typography>

              <Typography
                variant="subtitle1"
                component="div"
                sx={{ color: "text.secondary" }}
                className="video-preview-description"
              >
                {series.description}
              </Typography>
            </CardContent>
          </Box>
        </CardActionArea>
      </Card>
    );
  }

  function createWakeUpPreviewCard() {
    const releaseDate = new Date(
      WAKE_UP_RELEASE_TIME
    ).toLocaleString("en-AU", {
      timeZone: "Australia/Melbourne",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return (
      <Card
        key={WAKE_UP_SERIES_ID}
        className={`video-preview-card wake-up-preview-card ${
          wakeUpReleased ? "" : "locked-content"
        }`}
      >
        <CardActionArea
          className="video-preview-action-area"
          component={Link}
          to={`/1111`}
          aria-label={
            wakeUpReleased
              ? "SL33P"
              : "?????"
          }
        >
          <div className="video-preview-left wake-up-preview-left">
            <Chip
              icon={
                !wakeUpReleased ? (
                  <IoLockClosedSharp size={12} />
                ) : undefined
              }
              label={wakeUpReleased ? "SL33P" : "?????"}
              className="card-media-chip small-chip wake-up-status-chip"
            />

            <div className="wake-up-preview-art">
              <div
                className="wake-up-preview-spiral"
                aria-hidden="true"
              >
                ◎
              </div>

              <div className="wake-up-preview-art-title">
                ? ? ? ? ?
              </div>

              {!wakeUpReleased && (
                <div className="wake-up-preview-lock">
                  <IoLockClosedSharp size={19} />
                </div>
              )}
            </div>
          </div>

          <Box className="video-preview-right">
            <CardContent sx={{ flex: "1 0 auto" }}>
              <Typography
                component="div"
                variant="h5"
                className="video-preview-title wake-up-preview-title"
              >
                ?????
              </Typography>

              <Typography
                variant="subtitle1"
                component="div"
                className="video-preview-description"
              >
                {wakeUpReleased
                  ? "SL33P"
                  : "?????"}
              </Typography>

              <Typography
                component="div"
                className="wake-up-preview-action"
              >
                {wakeUpReleased
                  ? "SL33P"
                  : "????"}
              </Typography>
            </CardContent>
          </Box>
        </CardActionArea>
      </Card>
    );
  }

  if (!fileLibrary) {
    return (
      <div className="center-div">
        <p className="error">
          No video library data available.
        </p>
      </div>
    );
  }

  return (
    <div className="center-div">
      <h1>Videos</h1>

      {/* Dedicated teaser, independent of the CDN video metadata. */}
      {createWakeUpPreviewCard()}

      {fileLibrary.series
        .filter(
          (series) => series.id !== WAKE_UP_SERIES_ID
        )
        .map((series, index) =>
          createVideoPreviewCard(series, index)
        )}
    </div>
  );
};

export default ContentLibraryPage;